'use client';

import type { CSSProperties, FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCoins } from './icons';
import { playChickenSound } from './chickenPreload';
import { getPortalRoot } from './portalRoot';

type ChickenState = 'idle' | 'claiming' | 'claimed';

interface ChickenPosition {
  topPct: number;
  leftPct: number;
}

interface FloatingChickenProps {
  onClaim: () => void;
  onDespawn?: () => void;
  position?: ChickenPosition;
  state?: ChickenState;
  visibleMs?: number;
}

const SAFE_TOP_PCT = 14;
const SAFE_BOTTOM_PCT = 18;
const SAFE_SIDE_PCT = 12;
const FLY_OUT_MS = 420;
const FEATHER_FALL_MS = 2500;
const DEFAULT_VISIBLE_MS = 10_000;

const FEATHER_SRCS = ['/feather-1.webp', '/feather-2.webp', '/feather-3.webp'];

// Radial "shot" burst: bx/by = explosion peak (radial, by<0 = up), ex/ey = where
// gravity drags it to (ey always well below). Near-zero delays = one impact.
const FEATHERS: ReadonlyArray<{
  bx: string;
  by: string;
  ex: string;
  ey: string;
  rot: string;
  delay: string;
  dur: string;
}> = [
  { bx: '2px', by: '-118px', ex: '8px', ey: '150px', rot: '-540deg', delay: '0ms', dur: '1700ms' },
  { bx: '60px', by: '-98px', ex: '92px', ey: '170px', rot: '520deg', delay: '20ms', dur: '1850ms' },
  { bx: '-58px', by: '-96px', ex: '-86px', ey: '165px', rot: '-680deg', delay: '10ms', dur: '1800ms' },
  { bx: '98px', by: '-54px', ex: '140px', ey: '205px', rot: '600deg', delay: '30ms', dur: '1950ms' },
  { bx: '-100px', by: '-50px', ex: '-138px', ey: '200px', rot: '-460deg', delay: '0ms', dur: '1900ms' },
  { bx: '120px', by: '6px', ex: '162px', ey: '250px', rot: '700deg', delay: '40ms', dur: '2050ms' },
  { bx: '-118px', by: '2px', ex: '-158px', ey: '245px', rot: '-620deg', delay: '15ms', dur: '2000ms' },
  { bx: '92px', by: '58px', ex: '120px', ey: '300px', rot: '480deg', delay: '50ms', dur: '2150ms' },
  { bx: '-90px', by: '60px', ex: '-116px', ey: '300px', rot: '-560deg', delay: '25ms', dur: '2100ms' },
  { bx: '46px', by: '96px', ex: '64px', ey: '330px', rot: '640deg', delay: '60ms', dur: '2250ms' },
  { bx: '-42px', by: '100px', ex: '-58px', ey: '330px', rot: '-500deg', delay: '35ms', dur: '2200ms' },
  { bx: '6px', by: '120px', ex: '12px', ey: '350px', rot: '420deg', delay: '70ms', dur: '2300ms' },
];

const randomSafePosition = (): ChickenPosition => ({
  topPct: SAFE_TOP_PCT + Math.random() * (100 - SAFE_TOP_PCT - SAFE_BOTTOM_PCT),
  leftPct: SAFE_SIDE_PCT + Math.random() * (100 - SAFE_SIDE_PCT * 2),
});

export const FloatingChicken: FC<FloatingChickenProps> = ({
  onClaim,
  onDespawn,
  position,
  state = 'idle',
  visibleMs = DEFAULT_VISIBLE_MS,
}) => {
  const [mounted, setMounted] = useState(false);
  const [tapped, setTapped] = useState(false);
  const [flyingOut, setFlyingOut] = useState(false);
  const claimedRef = useRef(false);

  const resolvedPosition = useMemo(() => position ?? randomSafePosition(), [position]);

  useEffect(() => setMounted(true), []);

  // Controlled trigger: parent flips `state` to 'claiming'/'claimed' (storybook
  // claim story or a real mutation) → play the shed-and-fall without a tap.
  useEffect(() => {
    if (state === 'claiming' || state === 'claimed') {
      claimedRef.current = true;
      setTapped(true);
    }
  }, [state]);

  // Auto-despawn after `visibleMs` if never tapped → no claim.
  useEffect(() => {
    if (tapped) return;
    const timer = window.setTimeout(() => {
      setFlyingOut(true);
      window.setTimeout(() => onDespawn?.(), FLY_OUT_MS);
    }, visibleMs);
    return () => window.clearTimeout(timer);
  }, [tapped, visibleMs, onDespawn]);

  // The kill SFX fires the instant the chicken is shot (tap or controlled claim).
  useEffect(() => {
    if (tapped) playChickenSound();
  }, [tapped]);

  // After a tap: chicken poofs, feathers drift down, then notify the parent.
  useEffect(() => {
    if (!tapped) return;
    const despawnTimer = window.setTimeout(() => onDespawn?.(), FEATHER_FALL_MS);
    return () => window.clearTimeout(despawnTimer);
  }, [tapped, onDespawn]);

  const handleTap = () => {
    if (claimedRef.current) return;
    claimedRef.current = true;
    setTapped(true);
    onClaim();
  };

  if (!mounted) return null;

  const wrapperAnim = tapped
    ? 'animate-shot-chicken-poof'
    : flyingOut
      ? 'animate-shot-chicken-fly-out'
      : 'animate-shot-chicken-bounce-in';

  return createPortal(
    <div
      className="fixed z-[999] -translate-x-1/2 -translate-y-1/2"
      style={{ top: `${resolvedPosition.topPct}%`, left: `${resolvedPosition.leftPct}%` }}
    >
      <div className={wrapperAnim}>
        <button
          type="button"
          onClick={handleTap}
          disabled={tapped}
          aria-label="+500"
          className="relative block w-20 h-20 active:scale-95 transition-transform disabled:pointer-events-none"
        >
          <img
            src="/chicken-cs2.webp"
            alt=""
            aria-hidden
            draggable={false}
            className={`w-full h-full object-contain drop-shadow-[0_8px_20px_rgba(0,0,0,0.5)] ${tapped ? '' : 'animate-shot-chicken-idle'}`}
          />
        </button>
      </div>
      {tapped && (
        <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          {FEATHERS.map((feather, i) => (
            <img
              key={i}
              src={FEATHER_SRCS[i % FEATHER_SRCS.length]}
              alt=""
              aria-hidden
              draggable={false}
              className="animate-shot-feather-fall absolute block h-6 w-auto max-w-none opacity-0"
              style={
                {
                  '--fbx': feather.bx,
                  '--fby': feather.by,
                  '--fex': feather.ex,
                  '--fey': feather.ey,
                  '--frot': feather.rot,
                  animationDelay: feather.delay,
                  animationDuration: feather.dur,
                } as CSSProperties
              }
            />
          ))}
        </div>
      )}
      {tapped && (
        <div className="animate-shot-coin-pop pointer-events-none absolute left-1/2 -top-2 -translate-x-1/2 flex items-center gap-1 whitespace-nowrap">
          <span className="text-gold text-[22px] font-extrabold tabular-nums drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
            +500
          </span>
          <FaCoins className="text-gold" size={22} />
        </div>
      )}
    </div>,
    getPortalRoot(),
  );
};
